import { LocalProfile } from "@core/model/local-profile";
import { LocalRole } from "@core/model/local-role.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { PROFILE } from "@core/utility/settings";
import StorageService from "@shared/services/storage.service";
import SimpleHeader from "@shared/ui/navigation/simple-header/simple-header";
import SimpleSidebar, {
  SimpleMenu,
} from "@shared/ui/navigation/simple-sidebar/simple-sidebar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Sidebar } from "primereact/sidebar";
import { useCallback, useRef, useState } from "react";
import { BiSolidUserAccount } from "react-icons/bi";
import { BsCalendar2CheckFill } from "react-icons/bs";
import { FaBuilding, FaUserCircle } from "react-icons/fa";
import { FaStore } from "react-icons/fa";
import { FaUsers } from "react-icons/fa";
import { FaPersonFalling } from "react-icons/fa6";
import { FaUsersLine } from "react-icons/fa6";
import { IoSettings } from "react-icons/io5";
import { IoCardSharp } from "react-icons/io5";
import { MdBusinessCenter } from "react-icons/md";
import { MdAccountTree, MdDashboard } from "react-icons/md";
import { MdCampaign } from "react-icons/md";
import { RiShutDownLine } from "react-icons/ri";
import { RxHamburgerMenu } from "react-icons/rx";
import { TbArrowsTransferDown } from "react-icons/tb";
import { NavLink, useNavigate } from "react-router-dom";

import { appFeature } from "../../../app.feature";

const getLocalProfileFromStorage = () => {
  const profile = StorageService.load<LocalProfile>(PROFILE);
  return profile || null;
};

export function useAuthShellNavigation(logo: string, company: string) {
  const navigate = useNavigate();
  const { getUserRole, getAuthUser } = useUserIdentityContext();
  const profile = getLocalProfileFromStorage();
  const [mobileMenu, setMobileMenu] = useState(false);
  const overlayRef = useRef(null);
  const domainNavigation = [
    {
      title: "Dashboard",
      icon: <MdDashboard />,
      path: "",
    },
    {
      title: "Registration",
      icon: <FaUsers />,
      path: "registration",
    },
    {
      title: "Campaign",
      icon: <MdCampaign />,
      path: "campaign",
    },
    {
      title: "RewardEvent",
      icon: <BsCalendar2CheckFill />,
      path: "reward-event",
    },
    {
      title: "Customer",
      icon: <FaPersonFalling />,
      path: "customer",
    },
    {
      title: "Transaction",
      icon: <TbArrowsTransferDown />,
      path: "transaction",
    },
    {
      title: "Card-series",
      icon: <IoCardSharp />,
      path: "card-series",
    },
    {
      title: "Setting",
      icon: <IoSettings />,
      path: "setting",
    },
  ];

  const getDomainNavigation = useCallback(() => {
    const userRole = getUserRole();
    if (userRole?.name.toUpperCase() === LocalRole.Cashier) {
      return [
        {
          title: "Dashboard",
          icon: <MdDashboard />,
          path: "",
        },
        {
          title: "Registration",
          icon: <FaUsers />,
          path: "registration",
        },
        {
          title: "Campaign",
          icon: <MdCampaign />,
          path: "campaign",
        },
        {
          title: "Customer",
          icon: <FaPersonFalling />,
          path: "customer",
        },
        {
          title: "Transaction",
          icon: <TbArrowsTransferDown />,
          path: "transaction",
        },
      ];
    }

    return domainNavigation;
  }, []);

  const getAccountNavigation = useCallback(() => {
    const userCode = getAuthUser()?.userCode;
    const userRole = getUserRole();

    let accountNavigation = [
      {
        title: "My Account",
        icon: <FaUserCircle />,
        path: `account/${userCode}`,
      },
    ];

    if (userRole?.name.toUpperCase() === LocalRole.Admin) {
      accountNavigation = [
        ...accountNavigation,
        {
          title: "Tenant",
          icon: <FaBuilding />,
          path: "tenant",
        },
        {
          title: "Account",
          icon: <BiSolidUserAccount />,
          path: "account",
        },
        {
          title: "Role",
          icon: <MdAccountTree />,
          path: "role",
        },
        {
          title: "Branch",
          icon: <FaStore />,
          path: "branch",
        },
      ];
    }

    if (userRole?.name.toUpperCase() === LocalRole.Manager) {
      accountNavigation = [
        ...accountNavigation,
        {
          title: "My Business",
          icon: <MdBusinessCenter />,
          path: `tenant/${userRole.tenant}`,
        },
        {
          title: "Accounts",
          icon: <FaUsersLine />,
          path: "account",
        },
      ];
    }

    if (userRole?.name.toUpperCase() === LocalRole.Cashier) {
      accountNavigation = [
        ...accountNavigation,
        {
          title: "Accounts",
          icon: <FaUsersLine />,
          path: "account",
        },
      ];
    }

    return accountNavigation;
  }, [getUserRole]);

  const handleLogout = () => {
    navigate("/login");
  };
  const handleMobileMenu = () => {
    setMobileMenu(true);
  };

  const headerElement = (
    <SimpleHeader title={company} logo={logo} style={appFeature.theme.header}>
      <section
        className="flex gap-2 items-center p-2 rounded-lg cursor-pointer border border-transparent hover:border-gray-50 hover:bg-white"
        onClick={(e) => (overlayRef as any)?.current.toggle(e)}
      >
        <span className="text-2xl">
          <FaUserCircle />
        </span>
        <div className="flex flex-col">
          <span className="text-base font-bold">
            {profile?.name || "System"}
          </span>
          <span className="text-xs">{profile?.roleName || "Admin"}</span>
        </div>
      </section>
      <OverlayPanel ref={overlayRef}>
        {getAccountNavigation().map((item, id) => (
          <NavLink
            to={item.path}
            key={id}
            className="flex items-center gap-2 p-2 cursor-pointer rounded-lg hover:bg-gray-100 hover:text-green-800"
            onClick={(e) => (overlayRef as any).current.toggle(e)}
          >
            <span className="text-base">{item.icon}</span>
            <span>{item.title}</span>
          </NavLink>
        ))}
      </OverlayPanel>

      <section className="hidden md:inline-flex">
        <button
          className="flex gap-2 items-center hover:bg-gray-50 hover:bg-opacity-40 p-2 rounded-lg border border-gray-600"
          onClick={() => handleLogout()}
        >
          <span className="text-2xl">
            <RiShutDownLine />
          </span>
        </button>
      </section>

      <section className="inline-flex md:hidden">
        <button
          className="flex gap-2 items-center hover:bg-gray-50 hover:bg-opacity-40 p-2 rounded-lg border border-gray-600"
          onClick={() => handleMobileMenu()}
        >
          <span className="text-2xl">
            <RxHamburgerMenu />
          </span>
        </button>
      </section>
    </SimpleHeader>
  );

  const navigationElement = (
    <div id="AuthShellNavigation" className="auth-shell-navigation">
      {/* Desktop Navigation */}
      <SimpleSidebar hostClass="hidden md:inline-flex">
        {getDomainNavigation().map((item, id) => (
          <SimpleMenu
            key={id}
            title={item.title}
            icon={item.icon}
            path={item.path}
          />
        ))}
      </SimpleSidebar>

      {/* Mobile Navigation */}
      <Sidebar
        visible={mobileMenu}
        onHide={() => setMobileMenu(false)}
        fullScreen
      >
        <header className="flex w-full justify-center">
          <div className="flex items-center gap-2">
            <img src={logo} width="30px" />
            <h2>{company}</h2>
          </div>
        </header>

        <small className="block ml-4 mt-4">Menu</small>
        {getDomainNavigation().map((item, id) => (
          <SimpleMenu
            key={id}
            title={item.title}
            icon={item.icon}
            path={item.path}
            onClickCallback={() => setMobileMenu(false)}
          />
        ))}
        <SimpleMenu title="Logout" icon={<RiShutDownLine />} />
      </Sidebar>
    </div>
  );

  return {
    headerElement,
    navigationElement,
  };
}
